import { useWorkspaceNavigation } from "@/hooks/use-workspace-navigation";
import { getErrorMessage } from "@/lib/api-error";
import {
  ArrowLeft,
  ArrowUpDown,
  Search,
  CheckCircle,
  X,
  RotateCcw,
} from "lucide-react";
import { useState } from "react";
import { Input } from "../ui/input";
import { Skeleton } from "../ui/skeleton";
import { Checkbox } from "../ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

import { dealsApi } from "@/api/deals-api";
import { getStageText } from "@/lib/utils";
import { useWorkspaceStore } from "@/store/workspace-store";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { MoreHorizontal } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { BulkUnarchiveDialog } from "./bulk-unarchive-dialog";
import { CompanyAvatar } from "./company-avatar";
import { toast } from "sonner";

// Define the interface for the raw API response
interface FlaggedCompany {
  _id: string;
  company_name: string;
  industry: string[];
  stage: number;
  application_status?: string;
  company_files: Array<{
    file_type: string;
    file_url: string;
  }>;
  applicant_company: string;
  flagged: {
    created_on: string;
    flagged_by: {
      _id: string;
      first_name: string;
      last_name: string;
    };
  };
}

export default function ArchiveComponent() {
  const { navigate } = useWorkspaceNavigation();
  const { activeWorkspaceId, activeWorkspace } = useWorkspaceStore();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDealIds, setSelectedDealIds] = useState<Set<string>>(
    new Set()
  );
  const [isBulkUnarchiveModalOpen, setIsBulkUnarchiveModalOpen] =
    useState(false);
  const [sortConfig, setSortConfig] = useState<{
    key: "name" | "addedOn" | null;
    direction: "asc" | "desc";
  }>({ key: "addedOn", direction: "desc" });

  const { data: flaggedDeals } = useQuery<FlaggedCompany[]>({
    queryKey: ["flaggedDeals", activeWorkspaceId],
    queryFn: () => dealsApi.fetchFlaggedCompanies(activeWorkspaceId!),
    enabled: !!activeWorkspaceId,
  });

  const bulkUnarchiveMutation = useMutation({
    mutationFn: async () => {
      return dealsApi.bulkRemoveFlaggedCompany(
        activeWorkspaceId!,
        Array.from(selectedDealIds)
      );
    },
    onSuccess: async () => {
      toast.success(`Successfully unarchived ${selectedDealIds.size} deals`);
      queryClient.invalidateQueries({ queryKey: ["flaggedDeals"] });
      setSelectedDealIds(new Set());
    },
    onError: (error) => {
      console.error("Bulk unarchive failed:", error);
      toast.error(getErrorMessage(error, "Failed to unarchive deals"));
    },
  });

  const handleSelectAll = (checked: boolean) => {
    if (checked && flaggedDeals) {
      setSelectedDealIds(
        new Set(flaggedDeals.map((deal) => deal.applicant_company))
      );
    } else {
      setSelectedDealIds(new Set());
    }
  };

  const handleSelectDeal = (dealId: string, checked: boolean) => {
    const newSelection = new Set(selectedDealIds);
    if (checked) {
      newSelection.add(dealId);
    } else {
      newSelection.delete(dealId);
    }
    setSelectedDealIds(newSelection);
  };

  const clearSelection = () => {
    setSelectedDealIds(new Set());
  };

  const isAllSelected =
    selectedDealIds.size === (flaggedDeals?.length || 0) &&
    (flaggedDeals?.length || 0) > 0;
  const isIndeterminate =
    selectedDealIds.size > 0 &&
    selectedDealIds.size < (flaggedDeals?.length || 0);

  if (!flaggedDeals) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-[500px] w-full" />
      </div>
    );
  }

  // Filter flagged companies and apply search
  const flaggedDealsList = flaggedDeals.filter(
    (deal) =>
      searchQuery === "" ||
      deal.company_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSort = (key: typeof sortConfig.key) => {
    setSortConfig((current) => ({
      key,
      direction:
        current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedDeals = [...flaggedDealsList].sort((a, b) => {
    if (!sortConfig.key) return 0;

    if (sortConfig.key === "name") {
      return sortConfig.direction === "asc"
        ? a.company_name.localeCompare(b.company_name)
        : b.company_name.localeCompare(a.company_name);
    }

    if (sortConfig.key === "addedOn") {
      return sortConfig.direction === "asc"
        ? new Date(a.flagged.created_on).getTime() -
            new Date(b.flagged.created_on).getTime()
        : new Date(b.flagged.created_on).getTime() -
            new Date(a.flagged.created_on).getTime();
    }

    return 0;
  });

  const handleUnarchive = async (companyId: string) => {
    if (!activeWorkspaceId) return;
    try {
      await dealsApi.removeFlaggedCompany(activeWorkspaceId, companyId);
      queryClient.invalidateQueries({ queryKey: ["flaggedDeals"] });
    } catch (error) {
      console.error("Failed to unarchive deal:", error);
    }
  };

  const handleBulkUnarchive = async () => {
    await bulkUnarchiveMutation.mutateAsync();
  };

  const renderStatusBadge = (status?: string) => {
    const statusValue = status?.trim();
    const displayLabel = statusValue || "No status";
    const statusConfig = statusValue
      ? activeWorkspace?.statuses?.find(
          (workspaceStatus) => workspaceStatus.title === statusValue
        )
      : undefined;
    const baseClasses =
      "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium";

    if (statusConfig?.colour?.startsWith("#")) {
      return (
        <span
          className={baseClasses}
          style={{ backgroundColor: statusConfig.colour, color: "white" }}
        >
          {displayLabel}
        </span>
      );
    }

    const colorClasses =
      statusConfig?.colour && statusConfig.colour.trim().length > 0
        ? statusConfig.colour
        : "bg-muted text-muted-foreground";

    return (
      <span className={`${baseClasses} ${colorClasses}`}>{displayLabel}</span>
    );
  };

  return (
    <div className="flex h-full flex-col overflow-hidden bg-background">
      {/* Header */}
      <div className="shrink-0 border-b border-border px-6 py-4">
        <button
          type="button"
          onClick={() => navigate("/deals")}
          className="mb-2 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to pipeline
        </button>
        <h1 className="font-serif text-[32px] leading-[1.15] tracking-tight text-foreground">
          Archived deals
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Restore deals you&apos;ve archived from the pipeline.
        </p>
      </div>

      {/* Search */}
      <div className="shrink-0 px-6 py-2.5">
        <div className="relative w-full md:w-96">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search archived deals by name"
            className="h-8 w-full rounded-md border-border bg-background pl-9 pr-3 text-xs placeholder:text-muted-foreground/70 focus-visible:ring-1 focus-visible:ring-foreground/20 focus-visible:ring-offset-0"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Quick Actions Toolbar */}
      {selectedDealIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
          <div className="flex items-center gap-4 rounded-full border border-border bg-card px-6 py-3 shadow-lg animate-in slide-in-from-bottom-4 duration-300">
            {/* Selection Info */}
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <Badge variant="secondary" className="text-sm">
                {selectedDealIds.size} selected
              </Badge>
            </div>

            {/* Divider */}
            <div className="h-6 w-px bg-border" />

            {/* Quick Actions */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsBulkUnarchiveModalOpen(true)}
                disabled={bulkUnarchiveMutation.isPending}
                className="flex items-center gap-2"
              >
                <RotateCcw className="h-4 w-4" />
                Unarchive
              </Button>
            </div>

            {/* Clear Selection */}
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSelection}
              className="ml-2 p-2 h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-hidden px-6 pb-6">
        {/* Desktop table */}
        <div className="hidden h-full md:block">
          <div className="h-full overflow-auto rounded-lg border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <div className="relative">
                    <Checkbox
                      checked={isAllSelected}
                      onCheckedChange={handleSelectAll}
                      aria-label="Select all archived deals"
                    />
                    {isIndeterminate && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="w-2 h-2 bg-primary rounded-sm" />
                      </div>
                    )}
                  </div>
                </TableHead>
                <TableHead
                  onClick={() => handleSort("name")}
                  className="cursor-pointer hover:bg-muted w-[250px]"
                >
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger className="flex items-center">
                        Company Name{" "}
                        <ArrowUpDown className="inline h-4 w-4 ml-2" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>The name of the archived company</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </TableHead>
                <TableHead className="w-[200px]">Industry</TableHead>
                <TableHead className="w-[150px]">Status</TableHead>
                <TableHead className="w-[120px]">Stage</TableHead>
                <TableHead className="w-[120px]">Added By</TableHead>
                <TableHead className="w-[50px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedDeals.map((deal) => (
                <TableRow key={deal._id}>
                  <TableCell className="w-12">
                    <Checkbox
                      checked={selectedDealIds.has(deal.applicant_company)}
                      onCheckedChange={(checked) =>
                        handleSelectDeal(
                          deal.applicant_company,
                          checked as boolean
                        )
                      }
                      aria-label={`Select ${deal.company_name}`}
                    />
                  </TableCell>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <CompanyAvatar
                        companyFiles={deal.company_files}
                        alt={deal.company_name}
                        className="h-8 w-8 shrink-0 bg-background ring-1 ring-border"
                        iconClassName="h-4 w-4"
                      />
                      {deal.company_name}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {deal.industry?.length > 0 ? (
                        <>
                          {deal.industry.slice(0, 3).map((industry, i) => (
                            <Badge key={i} variant="secondary">
                              {industry}
                            </Badge>
                          ))}
                          {deal.industry.length > 3 && (
                            <Badge variant="secondary">
                              +{deal.industry.length - 3}
                            </Badge>
                          )}
                        </>
                      ) : (
                        <Badge variant="secondary">No industries</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="w-[150px]">
                    {renderStatusBadge(deal.application_status)}
                  </TableCell>
                  <TableCell>
                    {typeof deal.stage === "number"
                      ? getStageText(deal.stage)
                      : "N/A"}
                  </TableCell>
                  <TableCell>
                    {deal.flagged.flagged_by.first_name}{" "}
                    {deal.flagged.flagged_by.last_name}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() =>
                            handleUnarchive(deal.applicant_company)
                          }
                        >
                          Unarchive
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Mobile cards */}
      <div className="h-full md:hidden">
        <div className="h-full space-y-3 overflow-auto">
          {sortedDeals.map((deal) => (
            <div
              key={deal._id}
              className="space-y-3 rounded-lg border border-border bg-card p-4"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={selectedDealIds.has(deal.applicant_company)}
                    onCheckedChange={(checked) =>
                      handleSelectDeal(
                        deal.applicant_company,
                        checked as boolean
                      )
                    }
                    aria-label={`Select ${deal.company_name}`}
                  />
                  <div className="flex items-center gap-2 font-medium">
                    <CompanyAvatar
                      companyFiles={deal.company_files}
                      alt={deal.company_name}
                      className="h-8 w-8 shrink-0 bg-background ring-1 ring-border"
                      iconClassName="h-4 w-4"
                    />
                    {deal.company_name}
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => handleUnarchive(deal.applicant_company)}
                    >
                      Unarchive
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-muted-foreground mb-1">Status</div>
                  <div>{renderStatusBadge(deal.application_status)}</div>
                </div>
                <div>
                  <div className="text-muted-foreground mb-1">Stage</div>
                  <div>
                    {typeof deal.stage === "number"
                      ? getStageText(deal.stage)
                      : "N/A"}
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="text-muted-foreground mb-1">Added By</div>
                  <div>
                    {deal.flagged.flagged_by.first_name}{" "}
                    {deal.flagged.flagged_by.last_name}
                  </div>
                </div>
              </div>

              <div>
                <div className="text-muted-foreground text-sm mb-2">
                  Industry
                </div>
                <div className="flex flex-wrap gap-1">
                  {deal.industry?.length > 0 ? (
                    <>
                      {deal.industry.slice(0, 3).map((industry, i) => (
                        <Badge key={i} variant="secondary">
                          {industry}
                        </Badge>
                      ))}
                      {deal.industry.length > 3 && (
                        <Badge variant="secondary">
                          +{deal.industry.length - 3}
                        </Badge>
                      )}
                    </>
                  ) : (
                    <Badge variant="secondary">No industries</Badge>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      </div>

      {/* Bulk Unarchive Dialog */}
      <BulkUnarchiveDialog
        isOpen={isBulkUnarchiveModalOpen}
        onClose={() => setIsBulkUnarchiveModalOpen(false)}
        selectedDealIds={Array.from(selectedDealIds)}
        onUnarchive={handleBulkUnarchive}
        isLoading={bulkUnarchiveMutation.isPending}
      />
    </div>
  );
}
